import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Brand, ThemeToggle, Dawn } from "@/components/hm/Basics";
import { api, errorDetail, WELCOME_TEXT, SUCCESS_TEXT } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const AuthLayout = ({ title, children }) => (
  <div className="auth-wrap">
    <Dawn />
    <header className="wrap" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", height: 76 }}>
      <Brand /><ThemeToggle />
    </header>
    <main className="wrap" style={{ paddingBlock: "1.5rem 3rem" }}>
      <div className="auth-card hm-fade-up">
        <p className="text-muted" style={{ margin: 0, fontSize: 14 }} data-testid="auth-welcome">{WELCOME_TEXT}</p>
        <h1 className="hm-h2" style={{ marginTop: ".8rem", marginBottom: "1.8rem" }}>{title}</h1>
        {children}
      </div>
    </main>
    <p className="text-faint" style={{ textAlign: "center", fontSize: 13, paddingBottom: 24 }}>
      Heartmony · claridad para tu despertar · <Link to="/privacidad" className="underline underline-offset-4" data-testid="footer-privacy-link">Aviso de privacidad</Link>
    </p>
  </div>
);

const Field = ({ id, label, type, ...props }) => {
  const [show, setShow] = useState(false);
  const isPwd = type === "password";
  return (
    <div className="grid gap-2">
      <label htmlFor={id} className="hm-label">{label}</label>
      <div style={{ position: "relative" }}>
        <Input id={id} data-testid={`${id}-input`} className="hm-input" required autoCapitalize="none" autoCorrect="off"
          type={isPwd && show ? "text" : type} style={isPwd ? { paddingRight: 48 } : undefined} {...props} />
        {isPwd && (
          <button type="button" onClick={() => setShow((s) => !s)} data-testid={`${id}-toggle`}
            aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
            style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: 0, color: "var(--hm-muted)", padding: 6 }}>
            {show ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
      </div>
    </div>
  );
};

const Submit = ({ loading, id, children }) => (
  <button type="submit" disabled={loading} data-testid={id} className="btn btn-primary btn-block">{loading ? "Un momento..." : children}</button>
);

const ErrorMsg = ({ msg }) => (msg ? <p className="form-error" data-testid="auth-error">{msg}</p> : null);

const useForm = (init) => {
  const [form, setForm] = useState(init);
  return [form, (k) => (e) => setForm({ ...form, [k]: e.target.value })];
};

export function Register() {
  const [form, set] = useForm({ name: "", email: "", password: "" });
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return setError("Marca la casilla para poder crear tu cuenta.");
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/register", { ...form, consent });
      sessionStorage.setItem("hm_welcome", "1");
      signIn(data); nav("/inicio");
    } catch (err) { setError(errorDetail(err)); } finally { setLoading(false); }
  };

  return (
    <AuthLayout title="Crea tu espacio">
      <form onSubmit={submit} className="grid gap-5" data-testid="register-form">
        <Field id="register-name" label="Nombre" value={form.name} onChange={set("name")} autoComplete="given-name" />
        <Field id="register-email" label="Email" type="email" value={form.email} onChange={set("email")} autoComplete="email" />
        <Field id="register-password" label="Contraseña" type="password" minLength={6} value={form.password} onChange={set("password")} autoComplete="new-password" />
        <p className="text-faint" style={{ margin: "-8px 0 0", fontSize: 12.5 }}>Mínimo seis caracteres.</p>
        <div className="consent">
          <Checkbox id="consent" checked={consent} onCheckedChange={(v) => setConsent(v === true)} data-testid="register-consent-checkbox" className="mt-0.5" />
          <label htmlFor="consent">
            Usamos estos datos para darte acceso y enviarte la bienvenida.{" "}
            <Link to="/privacidad" target="_blank" className="text-accent font-semibold underline underline-offset-4" data-testid="register-privacy-link">Aviso de privacidad</Link>
          </label>
        </div>
        <ErrorMsg msg={error} />
        <Submit loading={loading} id="register-submit-button">Empezar ahora</Submit>
      </form>
      <p className="text-muted" style={{ textAlign: "center", fontSize: 14, marginTop: 22 }}>
        Ya tienes cuenta. <Link to="/entrar" className="text-accent font-semibold" data-testid="go-login-link">Entrar</Link>
      </p>
    </AuthLayout>
  );
}

export function Login() {
  const [form, set] = useForm({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      signIn(data); nav("/inicio");
    } catch (err) { setError(errorDetail(err)); } finally { setLoading(false); }
  };

  return (
    <AuthLayout title="Vuelve a tu espacio">
      <form onSubmit={submit} className="grid gap-5" data-testid="login-form">
        <Field id="login-email" label="Email" type="email" value={form.email} onChange={set("email")} autoComplete="email" />
        <Field id="login-password" label="Contraseña" type="password" value={form.password} onChange={set("password")} autoComplete="current-password" />
        <Link to="/recuperar" className="text-accent font-semibold" style={{ fontSize: 14, justifySelf: "end", marginTop: -6 }} data-testid="forgot-password-link">He olvidado mi contraseña</Link>
        <ErrorMsg msg={error} />
        <Submit loading={loading} id="login-submit-button">Entrar en mi espacio</Submit>
      </form>
      <p className="text-muted" style={{ textAlign: "center", fontSize: 14, marginTop: 22 }}>
        Aún no tienes cuenta. <Link to="/registro" className="text-accent font-semibold" data-testid="go-register-link">Crear cuenta</Link>
      </p>
    </AuthLayout>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { setError(errorDetail(err)); } finally { setLoading(false); }
  };

  return (
    <AuthLayout title="Recupera tu acceso">
      {sent ? (
        <p data-testid="forgot-sent" style={{ margin: 0 }}>Si hay una cuenta con ese email, te hemos enviado un enlace. Revisa tu correo. El enlace sirve durante una hora.</p>
      ) : (
        <form onSubmit={submit} className="grid gap-5" data-testid="forgot-form">
          <p className="text-muted" style={{ margin: 0, fontSize: 14 }}>Escribe tu email. Te enviamos un enlace para crear una contraseña nueva.</p>
          <Field id="forgot-email" label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
          <ErrorMsg msg={error} />
          <Submit loading={loading} id="forgot-submit-button">Enviar enlace</Submit>
        </form>
      )}
      <p style={{ textAlign: "center", fontSize: 14, marginTop: 22 }}>
        <Link to="/entrar" className="text-accent font-semibold" data-testid="forgot-back-login">Volver a entrar</Link>
      </p>
    </AuthLayout>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try { await api.post("/auth/reset-password", { token: params.get("token") || "", password }); setDone(true); }
    catch (err) { setError(errorDetail(err)); } finally { setLoading(false); }
  };

  return (
    <AuthLayout title="Crea tu contraseña nueva">
      {done ? (
        <div data-testid="reset-done" className="grid gap-5">
          <p style={{ margin: 0 }}>{SUCCESS_TEXT}</p>
          <Link to="/entrar" className="btn btn-primary btn-block" data-testid="reset-go-login">Entrar en mi espacio</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="grid gap-5" data-testid="reset-form">
          <Field id="reset-password" label="Contraseña nueva" type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
          <p className="text-faint" style={{ margin: "-8px 0 0", fontSize: 12.5 }}>Mínimo seis caracteres.</p>
          <ErrorMsg msg={error} />
          <Submit loading={loading} id="reset-submit-button">Guardar contraseña</Submit>
        </form>
      )}
    </AuthLayout>
  );
}
